import { Button, Chip, Spinner } from '@sjuni/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Eye,
  EyeOff,
  Plus,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FormError } from '@/components/FormError';
import { ApiRequestError, apiFetch } from '@/lib/api/client';
import { useEditorLocale } from '@/lib/editorLocale/useEditorLocale';
import { valueFor } from '@/lib/forms/translatable';

import { MenuItemForm } from './MenuItemForm';
import type { MenuItemInput, MenuItemRow, MenuPosition, MenuSummary, MenuTree } from './types';

/**
 * docs/05-ADMIN-DASHBOARD.md §5.6 — `/menus/:key`, the menu builder.
 *
 * "Nested drag-and-drop tree per menu key … Live validation flags items
 * pointing at unpublished or deleted records."
 *
 * The tree is moved with buttons rather than dragged. Four directions —
 * up, down, out, in — because that is what a nested reorder actually needs and
 * because a keyboard can reach all four; a pointer-only drag on a header of
 * thirty-four items would be both hard to aim and impossible to use without a
 * mouse. Each move sends the whole flattened tree, which is what the endpoint
 * takes and what keeps the live header from ever holding two items in one slot.
 */
export function MenusPage() {
  const rest = (useParams()['*'] ?? '').replace(/\/+$/, '');

  return rest === '' ? <List /> : <Builder menuKey={rest} />;
}

const KEY = ['admin', 'menus'];

function List() {
  const { t } = useTranslation();

  const menus = useQuery({
    queryKey: KEY,
    queryFn: () => apiFetch<{ data: MenuSummary[] }>('/admin/menus'),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-h2 font-extrabold text-primary">{t('nav.items.menus')}</h1>
        <p className="mt-1 text-body-sm text-secondary">{t('menus.intro')}</p>
      </div>

      {menus.isError ? <FormError message={t('errors.generic')} /> : null}
      {menus.isPending ? <Spinner size="lg" label={t('common.loading')} /> : null}

      <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {(menus.data?.data ?? []).map((menu) => (
          <li key={menu.key}>
            <Link
              to={`/menus/${menu.key}`}
              className="block rounded-card border border-subtle p-4 transition-colors hover:border-strong focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600"
            >
              <span className="block font-semibold text-primary">
                {t(`menus.keys.${menu.key}`, menu.name)}
              </span>
              <span className="mt-1 block text-caption text-muted">
                {t('menus.itemCount', { count: menu.items_count ?? 0 })}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Builder({ menuKey }: { menuKey: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [editing, setEditing] = useState<MenuItemRow | 'new' | null>(null);
  const [deleting, setDeleting] = useState<MenuItemRow | null>(null);
  const [error, setError] = useState<string | null>(null);

  const menu = useQuery({
    queryKey: [...KEY, menuKey],
    queryFn: () => apiFetch<{ data: MenuTree }>(`/admin/menus/${menuKey}`),
  });

  const linkables = useQuery({
    queryKey: [...KEY, 'linkables'],
    queryFn: () => apiFetch<{ data: string[] }>('/admin/menus/linkables'),
  });

  const items = menu.data?.data.items ?? [];

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: [...KEY, menuKey] });
  };

  const fail = (cause: unknown): void => {
    setError(cause instanceof ApiRequestError ? cause.message : t('errors.generic'));
  };

  const reorder = useMutation({
    mutationFn: (positions: MenuPosition[]) =>
      apiFetch(`/admin/menus/${menuKey}/reorder`, { method: 'POST', body: { items: positions } }),
    onSuccess: invalidate,
    onError: fail,
  });

  const save = useMutation({
    mutationFn: ({ id, input }: { id: number | null; input: MenuItemInput }) =>
      apiFetch(
        id === null ? `/admin/menus/${menuKey}/items` : `/admin/menus/${menuKey}/items/${id}`,
        {
          method: id === null ? 'POST' : 'PUT',
          body: input,
        },
      ),
    onSuccess: () => {
      setEditing(null);
      invalidate();
    },
    onError: fail,
  });

  const remove = useMutation({
    mutationFn: (id: number) =>
      apiFetch(`/admin/menus/${menuKey}/items/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setDeleting(null);
      invalidate();
    },
    onError: fail,
  });

  function move(item: MenuItemRow, direction: 'up' | 'down' | 'out' | 'in'): void {
    const flat = flatten(items);
    const siblings = flat.filter((row) => row.parent_id === item.parent_id);
    const index = siblings.findIndex((row) => row.id === item.id);

    if (direction === 'out') {
      const parent = flat.find((row) => row.id === item.parent_id);

      if (parent === undefined) {
        return;
      }

      // Out of its parent and immediately after it, which is where an editor
      // promoting an item expects to find it.
      reorder.mutate(reposition(flat, item.id, parent.parent_id, parent.sort_order + 1));

      return;
    }

    if (direction === 'in') {
      const above = siblings[index - 1];

      if (above === undefined) {
        return;
      }

      // Under the sibling above, at the end of its children — the only
      // unambiguous place for it.
      reorder.mutate(reposition(flat, item.id, above.id, childrenOf(flat, above.id).length));

      return;
    }

    const swap = siblings[direction === 'up' ? index - 1 : index + 1];

    if (swap === undefined) {
      return;
    }

    reorder.mutate(reposition(flat, item.id, item.parent_id, swap.sort_order));
  }

  if (menu.isPending) {
    return (
      <div className="grid min-h-96 place-items-center">
        <Spinner size="lg" label={t('common.loading')} />
      </div>
    );
  }

  const broken = flatten(items).filter((item) => item.link_status !== 'ok');

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-h2 font-extrabold text-primary">
            {t(`menus.keys.${menuKey}`, menu.data?.data.name ?? menuKey)}
          </h1>
          <p className="mt-1 text-body-sm text-secondary">{t('menus.builderIntro')}</p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            iconLeft={<Plus size={16} strokeWidth={1.75} aria-hidden="true" />}
            onClick={() => setEditing('new')}
          >
            {t('menus.newItem')}
          </Button>

          <Button type="button" variant="ghost" size="sm" onClick={() => navigate('/menus')}>
            {t('common.back')}
          </Button>
        </div>
      </div>

      <FormError message={error} />

      {/*
        §5.6's "live validation", said once at the top as well as per row: an
        editor who has not scrolled to the broken item has no reason to know it
        is there, and the public site is already hiding it.
      */}
      {broken.length > 0 ? (
        <p
          role="status"
          className="bg-accent-tint-gold flex items-start gap-2 rounded-card p-3 text-body-sm text-primary"
        >
          <AlertTriangle size={16} strokeWidth={1.75} aria-hidden="true" className="mt-0.5" />
          {t('menus.brokenWarning', { count: broken.length })}
        </p>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[1fr_400px]">
        <ol className="space-y-1">
          {flatten(items).map((item) => (
            <li
              key={item.id}
              style={{ marginInlineStart: `${depthOf(flatten(items), item) * 24}px` }}
              className={[
                'flex items-center gap-1 rounded-card border p-2',
                editing !== 'new' && editing?.id === item.id
                  ? 'bg-surface-subtle border-navy-600'
                  : 'border-subtle',
                /*
                 * A dashed border rather than `opacity-60`.
                 *
                 * Dimming the whole row took the already-muted link caption to
                 * 2.39:1 against the surface, which axe fails at `serious` —
                 * §7.2 of the design system wants 4.5:1 on body text and a
                 * hidden item is still text somebody has to read to decide
                 * whether to unhide it. The state now shows as a border and a
                 * chip, neither of which is text, beside the eye toggle that
                 * already carried it for screen readers.
                 */
                item.is_visible ? '' : 'border-dashed',
              ].join(' ')}
            >
              <button
                type="button"
                className="min-w-0 flex-1 text-start focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600"
                onClick={() => setEditing(item)}
              >
                <span className="flex items-center gap-2">
                  <span className="truncate font-semibold text-primary">
                    <ItemLabel item={item} />
                  </span>

                  {item.link_status === 'ok' ? null : (
                    <Chip tone="gold">{t(`menus.status.${item.link_status}`)}</Chip>
                  )}

                  {item.is_visible ? null : <Chip tone="neutral">{t('menus.hidden')}</Chip>}
                </span>

                <span className="truncate text-caption text-muted">
                  <ItemLink item={item} />
                </span>
              </button>

              <div className="flex shrink-0 items-center gap-0.5">
                {(
                  [
                    ['up', ChevronUp],
                    ['down', ChevronDown],
                    ['out', ChevronLeft],
                    ['in', ChevronRight],
                  ] as const
                ).map(([direction, Icon]) => (
                  <Button
                    key={direction}
                    type="button"
                    size="sm"
                    variant="ghost"
                    aria-label={t(`menus.move.${direction}`)}
                    disabled={reorder.isPending}
                    onClick={() => move(item, direction)}
                  >
                    <Icon size={14} strokeWidth={1.75} aria-hidden="true" />
                  </Button>
                ))}

                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  aria-pressed={item.is_visible}
                  aria-label={t(item.is_visible ? 'menus.hide' : 'menus.show')}
                  disabled={save.isPending}
                  onClick={() =>
                    save.mutate({
                      id: item.id,
                      input: { ...toInput(item), is_visible: !item.is_visible },
                    })
                  }
                >
                  {item.is_visible ? (
                    <Eye size={14} strokeWidth={1.75} aria-hidden="true" />
                  ) : (
                    <EyeOff size={14} strokeWidth={1.75} aria-hidden="true" />
                  )}
                </Button>

                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  aria-label={t('menus.removeItem')}
                  onClick={() => setDeleting(item)}
                >
                  <Trash2 size={14} strokeWidth={1.75} aria-hidden="true" />
                </Button>
              </div>
            </li>
          ))}

          {items.length === 0 ? (
            <li className="rounded-card border border-subtle p-4 text-body-sm text-secondary">
              {t('menus.empty')}
            </li>
          ) : null}
        </ol>

        {editing !== null ? (
          <section className="rounded-card border border-subtle p-4">
            <h2 className="mb-4 text-h4 font-extrabold text-primary">
              {editing === 'new' ? t('menus.newItem') : t('menus.editItem')}
            </h2>

            <MenuItemForm
              key={editing === 'new' ? 'new' : editing.id}
              item={editing === 'new' ? null : editing}
              aliases={linkables.data?.data ?? []}
              saving={save.isPending}
              onSave={(input) => save.mutate({ id: editing === 'new' ? null : editing.id, input })}
              onCancel={() => setEditing(null)}
            />
          </section>
        ) : null}
      </div>

      <ConfirmDialog
        open={deleting !== null}
        title={t('menus.delete.title')}
        // §8 gives `menu_items` no `deleted_at`, so this one really is gone —
        // and its children with it.
        body={t('menus.delete.body')}
        confirmLabel={t('common.delete')}
        reversible={false}
        busy={remove.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting) {
            remove.mutate(deleting.id);
          }
        }}
      />
    </div>
  );
}

function ItemLabel({ item }: { item: MenuItemRow }) {
  const { locale } = useEditorLocale();

  return <>{valueFor(item.label, locale) || '—'}</>;
}

/**
 * The item's link, in one line, for the row under its label.
 *
 * A hook rather than a helper taking `t`: i18next's `t` is generic over its
 * key, and a hand-written signature for it is both wrong and load-bearing.
 */
function ItemLink({ item }: { item: MenuItemRow }) {
  const { t } = useTranslation();

  if (item.linkable_type !== null) {
    return (
      <>{`${t(`menus.linkables.${item.linkable_type}`, item.linkable_type)} #${String(item.linkable_id ?? '')}`}</>
    );
  }

  return <>{item.route_name ?? item.url ?? '—'}</>;
}

/* ── the tree, flattened ─────────────────────────────────────────────── */

/**
 * Depth-first, which is the order the list is read in.
 *
 * The API returns the tree nested and the reorder endpoint takes it flat, so
 * one of the two conversions has to happen here. Flattening for display as well
 * means the indent and the move buttons work off the same array.
 */
function flatten(items: MenuItemRow[]): MenuItemRow[] {
  return items.flatMap((item) => [item, ...flatten(item.children)]);
}

function childrenOf(flat: MenuItemRow[], parentId: number): MenuItemRow[] {
  return flat.filter((item) => item.parent_id === parentId);
}

function depthOf(flat: MenuItemRow[], item: MenuItemRow): number {
  let depth = 0;
  let cursor = item.parent_id;

  // Bounded: three levels is what the API loads, and a bound is also the
  // answer to a cycle that somehow exists in the data.
  for (let guard = 0; guard < 5 && cursor !== null; guard++) {
    depth++;
    cursor = flat.find((row) => row.id === cursor)?.parent_id ?? null;
  }

  return depth;
}

/**
 * The whole tree's positions, with one item moved.
 *
 * Every sibling group is renumbered from zero afterwards, so a move never
 * leaves two items claiming the same slot — which is what the reorder endpoint
 * writes verbatim.
 */
function reposition(
  flat: MenuItemRow[],
  id: number,
  parentId: number | null,
  at: number,
): MenuPosition[] {
  const moved = flat.find((item) => item.id === id);

  if (moved === undefined) {
    return [];
  }

  const next = flat.map((item) =>
    item.id === id
      ? { ...item, parent_id: parentId, sort_order: at - 0.5 }
      : { ...item, sort_order: item.sort_order },
  );

  const positions: MenuPosition[] = [];
  const parents = [...new Set(next.map((item) => item.parent_id))];

  for (const parent of parents) {
    next
      .filter((item) => item.parent_id === parent)
      .sort((a, b) => a.sort_order - b.sort_order)
      .forEach((item, index) => {
        positions.push({ id: item.id, parent_id: item.parent_id, sort_order: index });
      });
  }

  return positions;
}

function toInput(item: MenuItemRow): MenuItemInput {
  return {
    label: item.label,
    description: item.description,
    parent_id: item.parent_id,
    linkable_type: item.linkable_type,
    linkable_id: item.linkable_id,
    route_name: item.route_name,
    url: item.url,
    target: item.target,
    icon: item.icon,
    is_visible: item.is_visible,
    sort_order: item.sort_order,
  };
}
